import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { Button, Column, Icon, Modal, Row, Text } from "@amzn/meridian";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { setScannedBarcode, setShowBarcodeConfirmation, addScannedBarcode, setImageCapturingMode, setCameraOpen } from "src/store/inspectionCaptureSlice";
import { COLORS } from "src/constants/colors";
import BarcodeConfirmationModal from "./BarcodeConfirmationModal";

const BarcodeScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { templateId } = useParams<{ templateId: string }>();
  
  const { scannedBarcode, showBarcodeConfirmation, scannedBarcodes } = useAppSelector(
    (state) => state.inspectionCapture
  );
  
  const { selectedInspectionTemplate } = useAppSelector(
    (state) => state.inspectionTemplate
  );
  
  const [isScanning, setIsScanning] = useState(false);
  const [lastScanTime, setLastScanTime] = useState(0);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateBarcode, setDuplicateBarcode] = useState("");

  // Debounce scans to prevent multiple triggers
  const SCAN_DEBOUNCE_MS = 2000;

  const handleScan = useCallback((result: any) => {
    if (result && result.length > 0) {
      const barcodeValue = result[0].rawValue;
      setIsScanning(false);
      
      // Check if this barcode has already been scanned
      if (scannedBarcodes.includes(barcodeValue)) {
        // Show duplicate alert
        setDuplicateBarcode(barcodeValue);
        setShowDuplicateAlert(true);
        
        // Haptic feedback for error
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]); // Double vibration for error
        }
        return;
      }
      
      // Store barcode in Redux and show confirmation modal
      dispatch(setScannedBarcode(barcodeValue));
      dispatch(setShowBarcodeConfirmation(true));
      
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
    }
  }, [dispatch, scannedBarcodes]);

  const handleError = useCallback((error: any) => {
    console.error("Camera error:", error);
  }, []);

  const handleBack = () => {
    navigate(`/templates/${templateId}`);
  };

  const handleConfirm = () => {
    dispatch(setShowBarcodeConfirmation(false));
    // Add barcode to scanned list
    if (scannedBarcode) {
      dispatch(addScannedBarcode(scannedBarcode));
    }
    // Set camera mode to active AND camera open before navigating back to inspection
    // This ensures the inspection page knows to start the camera
    dispatch(setImageCapturingMode(true));
    dispatch(setCameraOpen(true));
    // Navigate to inspection page
    navigate(`/templates/${templateId}/inspection`);
  };

  const handleRescan = () => {
    dispatch(setScannedBarcode(null));
    dispatch(setShowBarcodeConfirmation(false));
    setLastScanTime(0);
    setIsScanning(true);
  };

  const handleDuplicateAlertClose = () => {
    setShowDuplicateAlert(false);
    setDuplicateBarcode("");
    setIsScanning(true); // Resume scanning
  };

  useEffect(() => {
    setIsScanning(true);
  }, []);

  return (
    <Column
      height="100vh"
      width="100%"
      backgroundColor="#000000"
      alignmentVertical="justify"
    >
      {/* Header */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <Column
          width="100%"
          spacingInset="600 500 400"
        >
          <Row>
            <div
              style={{ cursor: "pointer", marginLeft: "50px" }}
              onClick={handleBack}
            >
              <Icon tokens={chevronLeftLargeTokens} color="inverted" />
            </div>
            {selectedInspectionTemplate?.templateName && (
              <Text color="inverted" type="b100">
                {selectedInspectionTemplate?.templateName}
              </Text>
            )}
          </Row>
          <Column spacing="300">
            <Row spacingInset="200 none">
              <Text color="inverted">Scan Drum Barcode</Text>
            </Row>
          </Column>
        </Column>
      </div>

      {/* Camera View - Full Page */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
        }}
      >
        <Scanner
          onScan={handleScan}
          onError={handleError}
          components={{ finder: false }}
          constraints={{
            facingMode: "environment",
            aspectRatio: 1, // Square aspect ratio
            // Advanced constraints
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          }}
        />
        
        {/* Scanning Frame Overlay */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: "500px",
            height: "40%",
            border: `3px solid ${isScanning ? "#0073BB" : "#00A650"}`,
            borderRadius: "8px",
            pointerEvents: "none",
          }}
        />

        {/* Status Text */}
        {isScanning && (
          <div
            style={{
              position: "absolute",
              bottom: "100px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: COLORS.WHITE,
              padding: "12px 24px",
              borderRadius: "8px",
            }}
          >
            <Text type="b100" color="secondary">
              Scanning...
            </Text>
          </div>
        )}
      </div>

      {/* Barcode Confirmation Modal */}
      <BarcodeConfirmationModal
        open={showBarcodeConfirmation}
        barcode={scannedBarcode || ""}
        onConfirm={handleConfirm}
        onRescan={handleRescan}
      />

      {/* Duplicate Barcode Alert Modal */}
      <Modal
        open={showDuplicateAlert}
        onClose={handleDuplicateAlertClose}
      >
        <Column spacing="400" spacingInset="500">
          <Column spacing="300" alignmentHorizontal="center">
            <Text type="h300" alignment="center">
              Drum Already Scanned
            </Text>
            <Text alignment="center" color="secondary">
              The barcode "{duplicateBarcode}" has already been scanned in this inspection session.
            </Text>
            <Text alignment="center" color="secondary">
              Please scan a different drum.
            </Text>
          </Column>
          <Row alignmentHorizontal="center" spacingInset="400 none none">
            <Button onClick={handleDuplicateAlertClose}>
              Scan Again
            </Button>
          </Row>
        </Column>
      </Modal>
    </Column>
  );
};

export default BarcodeScannerPage;
